import React, { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const Appointment = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date: '',
    time: '',
    service: '',
    notes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'الاسم مطلوب';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'رقم الهاتف مطلوب';
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'رقم الهاتف غير صحيح';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'البريد الإلكتروني مطلوب';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'البريد الإلكتروني غير صحيح';
    }
    
    if (!formData.date) {
      newErrors.date = 'التاريخ مطلوب';
    }
    
    if (!formData.time) {
      newErrors.time = 'الوقت مطلوب';
    }
    
    if (!formData.service) {
      newErrors.service = 'الخدمة مطلوبة';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (step === 1) {
      setStep(2);
      return;
    }

    try {
      // Submit the form
      toast({
        title: 'تم حجز الموعد بنجاح',
        description: 'سنقوم بالتواصل معك قريباً لتأكيد الموعد',
      });
      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        date: '',
        time: '',
        service: '',
        notes: ''
      });
      setStep(1);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'خطأ في الحجز',
        description: 'حدث خطأ أثناء حجز الموعد، يرجى المحاولة مرة أخرى',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-4">
              حجز موعد
            </h1>
            <p className="text-gray-600">
              {step === 1 ? 'يرجى ملء النموذج التالي لحجز موعدك' : 'يرجى مراجعة المعلومات قبل التأكيد'}
            </p>
          </div>

          <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {step === 1 ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="name">الاسم الكامل</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className={errors.name ? 'border-red-500' : ''}
                      />
                      {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">رقم الهاتف</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className={errors.phone ? 'border-red-500' : ''}
                      />
                      {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">البريد الإلكتروني</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className={errors.email ? 'border-red-500' : ''}
                      />
                      {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="service">الخدمة المطلوبة</Label>
                      <Select
                        value={formData.service}
                        onValueChange={(value) => setFormData({ ...formData, service: value })}
                      >
                        <SelectTrigger className={errors.service ? 'border-red-500' : ''}>
                          <SelectValue placeholder="اختر الخدمة" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cleaning">تنظيف الأسنان</SelectItem>
                          <SelectItem value="whitening">تبييض الأسنان</SelectItem>
                          <SelectItem value="filling">حشو الأسنان</SelectItem>
                          <SelectItem value="root-canal">علاج العصب</SelectItem>
                          <SelectItem value="crown">تركيب تاج</SelectItem>
                          <SelectItem value="implant">زراعة أسنان</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.service && <p className="text-red-500 text-sm">{errors.service}</p>}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="date">التاريخ</Label>
                        <Input
                          id="date"
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                          className={errors.date ? 'border-red-500' : ''}
                        />
                        {errors.date && <p className="text-red-500 text-sm">{errors.date}</p>}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="time">الوقت</Label>
                        <Input
                          id="time"
                          type="time"
                          value={formData.time}
                          onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                          className={errors.time ? 'border-red-500' : ''}
                        />
                        {errors.time && <p className="text-red-500 text-sm">{errors.time}</p>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes">ملاحظات إضافية</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="أي معلومات إضافية تود إضافتها..."
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold mb-4">تأكيد المعلومات</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">الاسم</p>
                        <p className="font-medium">{formData.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">رقم الهاتف</p>
                        <p className="font-medium">{formData.phone}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">البريد الإلكتروني</p>
                        <p className="font-medium">{formData.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">الخدمة</p>
                        <p className="font-medium">{formData.service}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">التاريخ</p>
                        <p className="font-medium">{new Date(formData.date).toLocaleDateString('ar-SA')}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">الوقت</p>
                        <p className="font-medium">{formData.time}</p>
                      </div>
                    </div>
                    {formData.notes && (
                      <div>
                        <p className="text-sm text-gray-500">ملاحظات إضافية</p>
                        <p className="font-medium">{formData.notes}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-between gap-4">
                  {step === 2 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(1)}
                    >
                      تعديل المعلومات
                    </Button>
                  )}
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
                  >
                    {step === 1 ? 'التالي' : 'تأكيد الحجز'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Appointment; 